import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {evaluateEventContainerConstraint} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import type {
    EventContainerConstraint,
    EventContainerScheduleCallDisableConfig,
} from "~/domainModels/workspaceModules/eventContainersModule";

function isEventContainerColumnValuePresent(value: unknown): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    if (typeof value === "string") {
        return value.trim() !== "";
    }
    return true;
}

function getEventContainerColumnValue(
    eventContainer: OttopilotEventContainer,
    refColumnId: Uuid | string,
    isAdditionalColumn: boolean,
): unknown {
    if (isAdditionalColumn === true) {
        return eventContainer.additionalColumnValues[refColumnId as Uuid];
    }
    return (eventContainer as unknown as Record<string, unknown>)[refColumnId];
}

/** Returns true when this rule says schedule call should be disabled. */
export function evaluateScheduleCallDisableRule(
    eventContainer: OttopilotEventContainer,
    rule: EventContainerConstraint,
    now: Date = new Date(),
): boolean {
    if (rule.type === "Present") {
        return isEventContainerColumnValuePresent(
            getEventContainerColumnValue(eventContainer, rule.refColumnId, rule.isAdditionalColumn),
        );
    }
    return evaluateEventContainerConstraint(eventContainer, rule, now) !== true;
}

export function isScheduleCallDisabled(
    eventContainer: OttopilotEventContainer,
    config: EventContainerScheduleCallDisableConfig,
    now: Date = new Date(),
): boolean {
    return config.rules.some((rule) => evaluateScheduleCallDisableRule(eventContainer, rule, now) === true);
}

export function getScheduleCallDisabledReason(
    eventContainer: OttopilotEventContainer,
    config: EventContainerScheduleCallDisableConfig | undefined,
    now: Date = new Date(),
): string | null {
    if (config === undefined) {
        return null;
    }
    if (isScheduleCallDisabled(eventContainer, config, now) !== true) {
        return null;
    }
    return config.disabledMessage;
}

export function createScheduleClickDisabledChecker<T>(
    config: EventContainerScheduleCallDisableConfig | undefined,
    getEventContainer: (row: T) => OttopilotEventContainer,
): ((row: T) => boolean) | undefined {
    if (config === undefined) {
        return undefined;
    }
    return (row: T) => getScheduleCallDisabledReason(getEventContainer(row), config) !== null;
}
